import classNames from "classnames";
import { FC, ReactNode, useContext } from "react";
import { format } from "date-fns";

import { MapContext } from "../Map/Map";

import NoDepartures from "../NoDepartures/NoDepartures";

import styles from "./RailAirDeparturesListCard.module.scss";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

export type RailAirDeparture = {
  scheduledDepartureTime: string;
  expectedDepartureTime: string;
  destination: string;
  service: string;
  platform?: string;
};

interface Props {
  dataQA: string;
  className?: string;
  departures: RailAirDeparture[];
}

const generateExpectedDepartureTime = (
  scheduledDepartureTime: string,
  expectedDepartureTime: string,
  t: TFunction<"translation", undefined>,
): ReactNode => {
  const scheduled = new Date(scheduledDepartureTime);
  const expected = new Date(expectedDepartureTime);

  if (scheduled < expected) {
    return (
      <span className={styles.delayed}>
        {t("rail_air_departures_list_card.time_expected", {
          time: format(expected, "HH:mm"),
        })}
      </span>
    );
  }

  return (
    <span className={styles.onTime}>
      {t("rail_air_departures_list_card.on_time")}
    </span>
  );
};

const generateDepartureListItems = (
  departures: RailAirDeparture[],
  t: TFunction<"translation", undefined>,
): ReactNode[] => {
  return departures.map((departure, index) => {
    return (
      <li className={styles.departureListItem} key={index}>
        <span className={styles.departureTime}>
          {format(new Date(departure.scheduledDepartureTime), "HH:mm")}
        </span>
        <div className={styles.serviceDetails}>
          <div className={styles.destinationAndPlatform}>
            <span className={styles.departure}>{departure.destination}</span>
            <span className={styles.platform}>
              {departure.platform
                ? t("rail_air_departures_list_card.platform", {
                    platform: departure.platform,
                  })
                : "—"}
            </span>
          </div>
          <div className={styles.operatorAndTime}>
            <span className={styles.operator}>{departure.service}</span>
            <span className={styles.time}>
              {generateExpectedDepartureTime(
                departure.scheduledDepartureTime,
                departure.expectedDepartureTime,
                t,
              )}
            </span>
          </div>
        </div>
      </li>
    );
  });
};

const RailAirDeparturesListCard: FC<Props> = ({
  dataQA,
  className,
  departures,
}) => {
  const { controlTheme } = useContext(MapContext);
  const { t } = useTranslation();

  const departureListItems = generateDepartureListItems(departures, t);

  return departureListItems.length ? (
    <ul
      data-qa={dataQA}
      className={classNames(styles.container, className, styles[controlTheme])}
    >
      {departureListItems}
    </ul>
  ) : (
    <NoDepartures title={t("rail_air_departures_list_card.no_departures")} />
  );
};

export default RailAirDeparturesListCard;
